import React, { useEffect, useState } from 'react';
import { MousePointerClick } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import BulletinCardWrapper from './BulletinCardWrapper';
import BulletinDeleteButton from './BulletinDeleteButton';
import BulletinBackButton from './BulletinBackButton';
import BulletinInteractionLayer from './BulletinInteractionLayer';
import BulletinMediaContent from './BulletinMediaContent';
import BulletinLikeHandler from './BulletinLikeHandler';
import BulletinLikeCorner from './BulletinLikeCorner';
import { useBulletinCard } from '@/hooks/use-bulletin-card';
import { useProfile } from '@/hooks/use-profile';
import BulletinActions from './BulletinActions';
import BulletinAuthor from './BulletinAuthor';
import { useMatureContent } from '@/hooks/use-mature-content';
import MechanicalCounter from '@/components/animations/MechanicalCounter';
import DropAnnouncementCard from '@/components/drops/DropAnnouncementCard';
import DropClaimOverlay from '@/components/drops/DropClaimOverlay';

interface BulletinCardProps {
  bulletin: any;
  size: 'small' | 'medium' | 'large' | 'horizontal';
  onLike?: (id: string) => void;
  onDelete?: () => void;
  isSelected?: boolean;
  onClose?: (id?: string) => void;
  clickCount?: number;
}

const BulletinCard = ({ 
  bulletin, 
  size, 
  onLike,
  onDelete,
  isSelected = false,
  onClose,
}: BulletinCardProps) => {
  const [isInitialRender, setIsInitialRender] = useState(true);
  
  // For detail view, we want to force image loading state to be true if already loaded once
  const [forcedImageLoaded, setForcedImageLoaded] = useState(false);
  const [showAura, setShowAura] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Drop claim overlay state
  const [showClaimOverlay, setShowClaimOverlay] = useState(false);
  const [claimCollectionId, setClaimCollectionId] = useState<string | null>(null);
  
  const {
    isHovered,
    setIsHovered,
    imageLoaded,
    setImageLoaded,
    isDeleting,
    setIsDeleting,
    showDoubleClickHint,
    handleClick,
    handleDoubleClick,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    setLongPressCallback,
    isClickInLikeCorner,
    handleCornerLike,
    isMobile
  } = useBulletinCard(isSelected, onClose, bulletin?.id);

  const { user } = useAuth();
  const { profile } = useProfile(user?.id);
  
  // Use the unified mature content hook
  const { showMatureContent } = useMatureContent();
  
  const canDelete = user?.id === bulletin?.user_id || profile?.is_admin;
  
  // Track first render to optimize animations
  useEffect(() => {
    if (isInitialRender) {
      setTimeout(() => setIsInitialRender(false), 10);
    }
  }, [isInitialRender]);
  
  // If transitioning to detail view, we want to preserve the loaded state
  useEffect(() => {
    if (isSelected && imageLoaded) {
      setForcedImageLoaded(true);
    }
  }, [isSelected, imageLoaded]);

  const userIsAdmin = !!bulletin?.profiles?.is_admin || !!bulletin?.user_is_admin;
  const userIsPremium = !!bulletin?.profiles?.is_premium || !!bulletin?.user_is_premium;

  const isMature = bulletin?.is_mature || 
                  (bulletin?.moderation_result?.isNSFW) || 
                  (bulletin?.moderation_status === 'sensitive');

  useEffect(() => {
    console.log(`🃏 [BULLETIN CARD] Render/Update ${bulletin?.id}:`, {
      isSelected,
      imageLoaded: imageLoaded || forcedImageLoaded,
      isMature,
      content: bulletin?.content?.substring(0, 30) + '...',
      has_image: !!bulletin?.image_url,
      moderation_status: bulletin?.moderation_status,
      profiles: bulletin?.profiles?.username,
      timestamp: new Date().toISOString()
    });
  }, [bulletin, isSelected, imageLoaded, forcedImageLoaded, isMature]);
                  
  // Handle image loading state
  const handleImageLoad = () => {
    console.log(`[BulletinCard ${bulletin?.id}] Image loaded`);
    setImageLoaded(true);
  };

  if (!bulletin) return null;

  // If this is a drop announcement, render the special drop card instead
  if (bulletin.is_drop_announcement && bulletin.drop_collection_id) {
    return (
      <>
        <DropAnnouncementCard 
          bulletin={bulletin}
          onClaim={(collectionId) => {
            setClaimCollectionId(collectionId);
            setShowClaimOverlay(true);
          }}
        />
        
        {showClaimOverlay && claimCollectionId && (
          <DropClaimOverlay
            collectionId={claimCollectionId}
            onClose={() => {
              setShowClaimOverlay(false);
              setClaimCollectionId(null);
            }}
          />
        )}
      </>
    );
  }

  return (
    <BulletinCardWrapper
      isSelected={isSelected}
      onImageClick={handleClick}
      onTouchStart={() => {}}
      onTouchEnd={handleTouchEnd}
      isAdmin={userIsAdmin}
      isPremium={userIsPremium}
    >
      {canDelete && (
        <BulletinDeleteButton
          bulletinId={bulletin.id}
          onDelete={onDelete}
          isDeleting={isDeleting}
          setIsDeleting={setIsDeleting}
        />
      )}
      <BulletinLikeHandler
        bulletinId={bulletin.id}
        initialLikeCount={bulletin.like_count || 0}
        userId={user?.id}
        currentImageUrl={bulletin.image_url}
        currentThumbnailUrl={bulletin.thumbnail_url}
        loopId={bulletin.loop_id}
      >
        {(hasLiked, currentLikeCount, onDoubleTap) => {
          // Set the callback for corner like functionality
          setLongPressCallback(onDoubleTap);

          return (
            <>
              <BulletinMediaContent
                bulletin={bulletin}
                size={size}
                imageError={imageError}
                imageLoaded={imageLoaded || forcedImageLoaded}
                onImageLoad={handleImageLoad}
                onImageError={() => setImageError(true)}
                isSelected={isSelected}
                isHovered={isHovered}
                getTextPosition={() => isSelected ? 'top-left' : 'center'}
                onDoubleClick={(e) => handleDoubleClick(e, onDoubleTap)}
                onTouchStart={(e) => handleTouchStart(e, onDoubleTap)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={isSelected ? undefined : handleClick}
                isMature={isMature}
                showMatureContent={showMatureContent}
              />
              
              {/* Like Corner */}
              <BulletinLikeCorner
                onLike={() => onDoubleTap(new Event('click') as any)}
                isLiked={!!hasLiked}
                isVisible={!isSelected && !hasLiked}
              />
              
              <BulletinInteractionLayer
                bulletin={bulletin}
                hasLiked={hasLiked}
                likeCount={currentLikeCount}
                isSelected={isSelected}
                onDoubleClick={(e) => {
                  console.log(`[BulletinCard ${bulletin?.id}] onDoubleClick fired on interaction layer`);
                  handleDoubleClick(e, onDoubleTap);
                  if (onLike && bulletin?.id) {
                    onLike(bulletin.id);
                  }
                  setShowAura(true);
                  setTimeout(() => setShowAura(false), 2000);
                }}
              />
              
              {/* Author positioned in bottom-left corner when liked in grid view */}
              {hasLiked && !isSelected && (
                <div
                  className="absolute bottom-4 left-4 z-50 flex items-center gap-2"
                  style={{ pointerEvents: 'auto' }}
                >
                  <BulletinAuthor
                    username={bulletin.profiles?.username ?? ''}
                    profileImageUrl={bulletin.profiles?.profile_image_url}
                    isAdmin={bulletin.profiles?.is_admin}
                    isPremium={bulletin.profiles?.is_premium}
                    isPartner={bulletin.profiles?.is_partner}
                  />
                  <BulletinActions
                    bulletinId={bulletin.id}
                    likeCount={currentLikeCount}
                    hasLiked={!!hasLiked}
                  />
                  {showAura && <MechanicalCounter value={bulletin.profiles?.aura_points || 0} />}
                </div>
              )}
              
              {/* Author positioned in bottom-left corner when selected and liked */}
              {hasLiked && isSelected && (
                <div
                  className="absolute bottom-4 left-4 z-50 flex items-center gap-2"
                  style={{ pointerEvents: 'auto' }}
                >
                  <BulletinAuthor
                    username={bulletin.profiles?.username ?? ''}
                    profileImageUrl={bulletin.profiles?.profile_image_url}
                    isAdmin={bulletin.profiles?.is_admin}
                    isPremium={bulletin.profiles?.is_premium}
                    isPartner={bulletin.profiles?.is_partner}
                  />
                  <BulletinActions
                    bulletinId={bulletin.id}
                    likeCount={currentLikeCount}
                    hasLiked={!!hasLiked}
                  />
                  {showAura && <MechanicalCounter value={bulletin.profiles?.aura_points || 0} />}
                </div>
              )}
            </>
          );
        }}
      </BulletinLikeHandler>
    </BulletinCardWrapper>
  );
};

export default BulletinCard;
